import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import { lazyInject } from '@app/containers/base/decorators/lazy-inject.decorator';
import { TransferGroupChildEditContainerComponent } from '@app/containers/transfer-group/containers/child-edit/edit.group-child.container';
import { TransferEditContainerComponent } from '@app/containers/transfer/containers/edit/edit.transfer.container';
import { CsvGenerateService } from '@app/shared/common/export-csv/csv.generate.service';
import { IPdfDefinitions } from '@app/shared/common/make-pdf/v2/pdf.type';
import {
  IEvent,
  TRANSFER_PDF_HEADERS,
  TRANSFER_PDF_HEADERS_FINAL,
  TRANSFER_TYPE,
  IBaseEntityType,
  TRANSFER_PDF_HEADER_VALUES,
} from '@app/shared/types';
import { TRANSFER_STATUS_VALUE } from '@app/shared/types/enums/transfer-status.enum';
import { isFunction, set, merge, find, get, assign, includes } from 'lodash';
import { XprtDataService } from '../xprt-data/xprt-data.service';
import { TRANSFER_FORM_FIELD_PATHS } from './transfer-form-field-paths';
import { getTransferPdfConfigBuilder } from './transfer-pdf-config';
import { TransferTotalFormService } from './transfer-totals-form.service';
import { TransferPdfService } from './transfer.pdf.service';
import {
  addParQtyColumn,
  addQtyPulledColumn,
  filterQohFromColumn,
} from './transfer.util';
import { XVII18nService } from '@app/i18n/services';

@Injectable()
export class TransferCsvService {
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(TransferTotalFormService)
  public totalFromService: TransferTotalFormService;
  @lazyInject(TransferPdfService) public transferPdfService: TransferPdfService;
  @lazyInject(CsvGenerateService) public csvGenerateService: CsvGenerateService;
  @lazyInject(XVII18nService) public i118nService: XVII18nService;

  public events: IEvent[];

  public constructor(public injector: Injector) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public async handleCsvClick(
    editor:
      | TransferEditContainerComponent
      | TransferGroupChildEditContainerComponent,
    useDocument: boolean = false,
    additionDoc: IBaseEntityType = {},
    options?: {
      isTransferGroup?: boolean;
      totalsConfiguration?: {
        enabled: boolean;
        getDefaultValue?: (data: any[]) => boolean;
      };
    },
  ): Promise<void> {
    const {
      isTransferGroup = false,
      totalsConfiguration = {
        enabled: true,
        getDefaultValue: () => false,
      },
    } = options || {};

    this.events = await this.transferPdfService.getEvents();
    const currentStatus: TRANSFER_STATUS_VALUE = editor.getFormFieldValue(
      editor.formFieldPath.status,
    );
    const transferType: TRANSFER_TYPE = editor.getFormFieldValue(
      editor.formFieldPath.transferType,
    );
    const isNotFinalTransfer = this.transferPdfService.isNotFinalTransfer(
      currentStatus,
      transferType,
    );
    let modalColumns = isNotFinalTransfer
      ? [...TRANSFER_PDF_HEADERS]
      : [...TRANSFER_PDF_HEADERS_FINAL];
    if (!editor?.showQohFrom) {
      modalColumns = filterQohFromColumn(modalColumns);
    }
    modalColumns = addParQtyColumn(modalColumns, currentStatus);
    modalColumns = addQtyPulledColumn(modalColumns, {
      transferStatus: currentStatus,
      preColumnPath: isNotFinalTransfer
        ? TRANSFER_PDF_HEADER_VALUES.Quantity
        : TRANSFER_PDF_HEADER_VALUES.InitialQuantity,
    });

    const totalFieldValue = !includes(
      [TRANSFER_STATUS_VALUE.Open, TRANSFER_STATUS_VALUE.Pending],
      currentStatus,
    );

    totalsConfiguration.getDefaultValue = () => totalFieldValue;

    const exportConfig = getTransferPdfConfigBuilder(
      TRANSFER_FORM_FIELD_PATHS,
      {
        isFromDetail: true,
        totalsConfiguration,
      },
    )(
      async (definitions: IPdfDefinitions) => {
        const isFromDetail = true;
        let doc = merge(
          { internal_number: editor.document?.internal_number },
          editor.form.value,
        );
        if (useDocument) {
          doc = merge({}, editor.form.value, {
            [TRANSFER_FORM_FIELD_PATHS.siteNameTo]: get(
              editor.document,
              TRANSFER_FORM_FIELD_PATHS.siteNameTo,
            ),
            [TRANSFER_FORM_FIELD_PATHS.storeNumberTo]: get(
              editor.document,
              TRANSFER_FORM_FIELD_PATHS.storeNumberTo,
            ),
            [TRANSFER_FORM_FIELD_PATHS.siteIdTo]: get(
              editor.document,
              TRANSFER_FORM_FIELD_PATHS.siteIdTo,
            ),
            [TRANSFER_FORM_FIELD_PATHS.transactionDate]: get(
              editor.document,
              TRANSFER_FORM_FIELD_PATHS.transactionDate,
            ),
          });
        }
        const docEvent = find(
          this.events,
          (event: IEvent) => event.id === doc.event_id,
        );
        set(doc, 'event_id', docEvent?.name);
        set(doc, 'displayQoh', editor?.showQohFrom);

        // apply addition doc
        assign(doc, additionDoc);

        this.transferPdfService.setPendingQty(
          editor.entityFormService.pendingQuantityData,
        );
        this.transferPdfService.setQOHData(editor.entityFormService.qohData);

        return this.transferPdfService.getPdfData(
          doc,
          {
            isFromDetail,
            docEvent,
            xuxGrid: editor.entityFormService.xuxGrid,
          },
          definitions?.options,
        );
      },
      modalColumns,
      null,
      {
        isTransferGroup,
        i18nService: this.i118nService,
      },
    );

    await this.csvGenerateService.downloadCsvAsync(exportConfig);
  }
}
